<template>
  <div class="v-select-wrapper w-full">
    <component
      ref="autocomplete"
      :class="[
        getLabel ? 'v-autocomplete--with-label' : '',
        multiple && (someItemsSelected || allItemsSelected)
          ? 'v-autocomplete--items-selected'
          : '',
        search && search.length ? 'v-autocomplete--search-added' : '',
      ]"
      :is="taggable ? 'v-combobox' : 'v-autocomplete'"
      v-bind="commonProps"
      :filled="filled"
      :multiple="multiple"
      :chips="chips"
      :small-chips="smallChips"
      :loading="loading"
      :item-text="getItemText"
      :item-value="getItemValue"
      :items="items || choices"
      :search-input.sync="search"
      @change="change"
      @input="update"
      @blur="blur"
      persistent-placeholder
      :prepend-inner-icon="multiple ? 'mdi-plus' : prependInnerIcon"
      :hide-details="'auto'"
      :cache-items="cacheItems"
      :disabled="disabled"
      :append-icon="'mdi-chevron-down'"
      :validate-on-blur="validateOnBlur"
    >
      <template v-slot:label>
        {{ getLabel }}
        <span v-if="required" class="required__icon">
          <slot name="required-symbol">*</slot>
        </span>
        <slot name="info"></slot>
      </template>

      <template v-slot:item="{ item, title, on }" v-if="customItem">
        <slot name="item" :item="item" :title="title" :on="on"></slot>
      </template>

      <template v-slot:no-data>
        <slot v-if="alternativeDropdownView" name="no-data"> </slot>
        <div v-else class="v-list-item theme--light">
          <div class="v-list-item__content">
            <div class="v-list-item__title">
              {{ itemsEmpty ? "No results found" : "Start typing to search" }}
            </div>
          </div>
        </div>
      </template>
      <template v-slot:append-item>
        <v-list-item
          v-if="showLoadMoreCondition()"
          ripple
          @mousedown.prevent
          @click.prevent="endIntersect(null, null, true)"
        >
          <v-list-item-content>
            <v-list-item-title class="show-more-title">{{
              $t("va.actions.show_more")
            }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <div v-else-if="!showLoadMore" v-intersect="endIntersect" />
        <slot name="append-item-outer"></slot>
      </template>

      <template
        v-if="!hideSelectAll && multiple && this.inputOptions.length"
        v-slot:prepend-item
      >
        <v-list-item
          :class="allItemsSelected ? 'v-list-item--active' : ''"
          ripple
          @mousedown.prevent
          @click="toggle"
        >
          <v-list-item-action>
            <v-icon
              :color="allItemsSelected || someItemsSelected ? 'primary' : ''"
            >
              {{ icon }}
            </v-icon>
          </v-list-item-action>
          <v-list-item-content>
            <v-list-item-title> Select all </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </template>

      <template v-if="multiple" v-slot:selection="{ item }">
        <slot name="selection" :item="item"></slot>
      </template>
    </component>
    <template v-if="multiple">
      <div
        :class="[
          'd-flex',
          'flex-wrap',
          'selection-chip__wrapper',
          selectedItems.length ? 'selection-chip__wrapper--has-selection' : '',
        ]"
      >
        <template v-if="!allItemsSelected">
          <template v-for="item in selectedItems">
            <v-chip
              class="selection-chip"
              :key="`${item[getItemValue] || item}-key`"
            >
              <span class="pr-2">
                {{ item[getItemText] || item }}
              </span>
              <v-icon small @click="$refs['autocomplete'].selectItem(item)"
                >mdi-close</v-icon
              >
            </v-chip>
          </template>
        </template>
        <template v-else-if="inputOptions.length && allItemsSelected">
          <v-chip class="selection-chip" :key="`all-selected-key`">
            <span class="pr-2">
              All
              <span class="text-lowercase">{{ getLabel }}</span>
            </span>
            <v-icon small @click="toggle">mdi-close</v-icon>
          </v-chip>
        </template>
      </div>
    </template>
  </div>
</template>

<script>
import Input from "../../../mixins/input";
import Multiple from "../../../mixins/multiple";
import ReferenceInput from "../../../mixins/reference-input";
import debounce from "lodash/debounce";

/**
 * Value editing from a searchable choices. Support multiple and references.
 * Allows searching of linked resources from your API.
 */
export default {
  mixins: [Input, Multiple, ReferenceInput],
  props: {
    validateOnBlur: {
      type: Boolean,
      default: false,
    },
    /**
     * Possibility to change view in no-data slot
     */
    alternativeDropdownView: {
      type: Boolean,
      default: false,
    },
    /**
     * Minimum characters to tap before search query launch.
     */
    minChars: {
      type: Number,
      default: 3,
    },
    /**
     * Name of request query for searching into your API.
     */
    searchQuery: {
      type: String,
      default: "q",
    },
    /**
     * Enable taggable mode. Transform autocomplete into combobox.
     */
    taggable: Boolean,
    cacheItems: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    prependInnerIcon: {
      type: String,
      default: "",
    },
    /**
     * Loads all the items on mount
     */
    preloadAllItems: {
      type: Boolean,
      default: false,
    },
    hideSelectAll: {
      type: Boolean,
      default: false,
    },
    showLoadMore: {
      type: Boolean,
      default: false,
    },
    // Don't load items if disabled
    emptyDisabled: {
      type: Boolean,
      default: false,
    },
    // Append item to fetch results
    appendItem: {
      type: Object,
      default: () => {},
    },
    // Enable custom item slot
    customItem: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      search: null,
      // Indicates that items have been preloaded (When initial v-model value is provided)
      itemsPreLoaded: false,
      itemsEmpty: false,
      page: 1,
      itemsLoaded: false,
      menu1: false,
    };
  },
  async beforeMount() {
    if (this.disabled && this.emptyDisabled) return;

    if (this.appendItem && this.input == this.appendItem?.["@id"]) {
      this.items = [this.appendItem];

      if (this.appendItem.hidden) {
        this.itemsEmpty = true;
      }

      return;
    }

    if (this.reference && this.input && this.showLoadMore) {
      this.items = await this.fetchCurrentChoices(
        this.multiple ? this.input : [this.input]
      );
    } else if (this.showLoadMore) {
      this.items = await this.fetchChoices(this.input);
    } else {
      this.preloadAllItems &&
        (this.items = await this.fetchChoices(this.input));
    }
    if (!this.items?.length) {
      this.itemsEmpty = true;
    }
    if (this.appendItem) {
      this.items.push(this.appendItem);
    }
  },
  mounted() {
    // Whenever we use 'multi' select on autocomplete, we have to set data-placeholder on v-select__selections, as it
    // is used to keep the placeholder displayed when there are some values selected.
    if (this.multiple) {
      this.$refs["autocomplete"]?.$el &&
        this.$refs["autocomplete"]?.$el
          .querySelector(".v-select__selections")
          .setAttribute("data-placeholder", this.placeholder);
    }
  },
  methods: {
    showLoadMoreCondition() {
      if (this.appendItem) {
        return (
          this.showLoadMore &&
          this.items &&
          this.totalItems > this.items?.length - 1
        );
      }
      return (
        this.showLoadMore && this.items && this.totalItems > this.items?.length
      );
    },
    async loadCurrentChoices(value) {
      if (this.reference && value) {
        let items = await this.fetchCurrentChoices(
          this.multiple ? value : [value]
        );
        /**
         * https://vuetifyjs.com/en/api/v-autocomplete/#props
         * Apparently, if an item has a property named 'header',
         * it becomes unselectable. That's why we have to remove
         * property 'header' from all the items
         */
        Array.isArray(items) &&
          (items = items.map((item) => {
            delete item.header;
            return item;
          }));
        this.items = items;

        if (this.appendItem && !this.items.includes(this.appendItem)) {
          this.items.push(this.appendItem);
        }
      }
    },
    async endIntersect(entries, observer, isIntersecting) {
      if (this.itemsLoaded || !this.items) {
        return;
      }
      if (isIntersecting) {
        this.page += 1;
        let items = await this.fetchChoices(this.search, this.page);
        if (!items || items.length === 0) {
          this.itemsLoaded = true;
          return;
        }
        this.items = [...this.items, ...items];
      }
    },
    blur(e) {
      if (this.$listeners.blur) {
        this.$listeners.blur(e);
      }
    },
  },
  watch: {
    /**
     * For now, make this watcher execute only once, when v-model of an autocomplete is set.
     * Otherwise, whenever an item is selected, a request is made. If there's 20 items inside of autocomplete
     * and user clicks "select all", then 20 requests are made.
     *
     * This watcher is required to pre-load selected items.
     * Let's say on page load, 2 items are selected (2 IRIs/IDs are passed via v-model),
     * va-autocomplete doesn't know any other properties of the IRIs (e.g. if it's a page IRI/ID, autocomplete doesn't know the page headline).
     * That is because va-autocomplete only fetches the items once user inputs something to the search field.
     */
    input: {
      handler(newVal) {
        if (this.itemsPreLoaded || this.preloadAllItems) return;

        if (this.appendItem && newVal == this.appendItem?.["@id"]) {
          this.items = [this.appendItem];

          if (this.appendItem.hidden) {
            this.itemsEmpty = true;
          }

          return;
        }
        /**
         * Fetch full object as soon as we get input value
         */
        this.loadCurrentChoices(newVal);
        this.itemsPreLoaded = true;
      },
      immediate: true,
    },
    search: debounce(async function (val, old) {
      // If all items are already loaded, don't trigger the network call to search.
      if (this.preloadAllItems) return;
      if (val?.length < this.minChars) {
        this.items = [];
        this.itemsEmpty = true;
        return;
      }
      this.itemsLoaded = false;
      this.page = 1;
      this.items = [...((await this.fetchChoices(val)) || [])];
      if (!this.items?.length) {
        this.itemsEmpty = true;
      }
      if (this.appendItem && !this.items.includes(this.appendItem)) {
        this.items.push(this.appendItem);
      }
    }, 400),
  },
};
</script>
